import { useCallback, useEffect, useRef } from 'react';

/**
 * useScrollFade
 *
 * Returns a ref callback to attach to a scroll container. While the container
 * is scrollable, it toggles two data-attributes on the element:
 *
 *   data-fade-top    — present only when scrolled down from the top
 *   data-fade-bottom — present only when there is more content below
 *
 * CSS keys off these attributes to render a soft edge fade (see the
 * `.scroll-fade-container[data-fade-*]` rules in forms.css and the
 * `.mobile-drawer-content[data-fade-*]` rules in @charitystack/shared
 * mobile-drawer.css). The point of doing this in JS rather than a static CSS
 * mask is correctness: no fade is shown at the very top, and no fade is shown
 * when the content does not overflow at all.
 *
 * Recomputes on scroll, on container/content resize (ResizeObserver), and on
 * DOM mutations inside the container (MutationObserver) — the latter matters
 * because form pages swap content of different heights without the container
 * itself resizing.
 *
 * @param {boolean} enabled - when false, the hook detaches and clears attrs.
 * @returns {(node: HTMLElement | null) => void} ref callback
 */
export function useScrollFade(enabled = true) {
  const nodeRef = useRef(null);
  const cleanupRef = useRef(null);

  const update = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;

    if (!enabled) {
      el.removeAttribute('data-fade-top');
      el.removeAttribute('data-fade-bottom');
      return;
    }

    // +1 / -2 tolerances absorb sub-pixel rounding and momentum overscroll.
    const canScroll = el.scrollHeight > el.clientHeight + 1;
    const isAtTop = el.scrollTop <= 2;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;

    const showTop = canScroll && !isAtTop;
    const showBottom = canScroll && !isAtBottom;

    if (showTop) el.setAttribute('data-fade-top', '');
    else el.removeAttribute('data-fade-top');

    if (showBottom) el.setAttribute('data-fade-bottom', '');
    else el.removeAttribute('data-fade-bottom');
  }, [enabled]);

  const setRef = useCallback(
    (node) => {
      // Tear down any prior wiring (ref reassigned or unmount).
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      nodeRef.current = node;
      if (!node) return;

      node.addEventListener('scroll', update, { passive: true });

      let resizeObserver;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(node);
        Array.from(node.children).forEach((child) => resizeObserver.observe(child));
      }

      let mutationObserver;
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver((mutations) => {
          if (resizeObserver) {
            mutations.forEach((m) => {
              m.addedNodes.forEach((n) => {
                if (n.nodeType === Node.ELEMENT_NODE) resizeObserver.observe(n);
              });
            });
          }
          update();
        });
        mutationObserver.observe(node, { attributes: false, childList: true, subtree: true });
      }

      // Initial compute, plus one after layout settles (content/fonts).
      update();
      const rafId =
        typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(update) : null;

      cleanupRef.current = () => {
        node.removeEventListener('scroll', update);
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
        if (rafId != null && typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(rafId);
        }
        // Clear the fade attributes so no stale fade lingers if the node is
        // reused (and to stay symmetric with DonorInfo.attachScrollFade).
        node.removeAttribute('data-fade-top');
        node.removeAttribute('data-fade-bottom');
      };
    },
    [update],
  );

  // Re-run when `enabled` flips so attrs are added/cleared without remount.
  useEffect(() => {
    update();
  }, [update]);

  return setRef;
}

export default useScrollFade;
